import { GraphicInterface, OverlayUploadData } from '../reducers/graphics';
import {
	addGraphics,
	bulkEditGraphics,
	deleteGraphics,
	loadGraphics,
	duplicateOverlayRequest,
} from '../../connection/graphics';
import { Action } from '../types/types';
import { GeneratedContent } from '../types/content';
import { EntityType } from '../../types/enums';

export const ADD_GRAPHICS = 'ADD_GRAPHICS';
export const APPEND_GRAPHICS = 'APPEND_GRAPHICS';
export const EDIT_GRAPHICS = 'EDIT_GRAPHICS';
export const UPDATE_GRAPHICS = 'UPDATE_GRAPHICS';
export const DELETE_GRAPHICS = 'DELETE_GRAPHICS';
export const LOAD_GRAPHICS = 'LOAD_GRAPHICS';
export const SET_CURRENT_GRAPHICS_ID = 'SET_CURRENT_GRAPHICS_ID';
export const SHOW_GRAPHIC_MODAL = 'SHOW_GRAPHIC_MODAL';
export const HIDE_GRAPHIC_MODAL = 'HIDE_GRAPHIC_MODAL';
export const REMOVE_GRAPHICS = 'REMOVE_GRAPHICS';
export const SET_ENABLED_GRAPHICS = 'SET_ENABLED_GRAPHICS';
export const ADD_ENABLED_GRAPHIC = 'ADD_ENABLED_GRAPHIC';
export const REMOVE_ENABLED_GRAPHIC = 'REMOVE_ENABLED_GRAPHIC';
export const REMOVE_SHOT_MODAL_GRAPHIC = 'REMOVE_SHOT_MODAL_GRAPHIC';
export const BULK_EDIT_GRAPHICS = 'BULK_EDIT_GRAPHICS';
export const SYNC_GRAPHICS_REDUCER_STATE = 'SYNC_GRAPHICS_REDUCER_STATE';
export const PLAYING_PREVIEW_ANIMATION = 'PLAYING_PREVIEW_ANIMATION';
export const PREPEND_GRAPHICS = 'PREPEND_GRAPHICS';
export const ADD_OVERLAYS_UPLOAD_DATA = 'ADD_OVERLAYS_UPLOAD_DATA';
export const DELETE_OVERLAYS_UPLOAD_DATA = 'DELETE_OVERLAYS_UPLOAD_DATA';
export const SET_ANIMATED_GRAPHICS = 'SET_ANIMATED_GRAPHICS';
export const ADD_ANIMATED_GRAPHIC = 'ADD_ANIMATED_GRAPHIC';
export const HIDE_GRAPHIC_FOR_SHOWBUILDER = 'HIDE_GRAPHIC_FOR_SHOWBUILDER';
export const SHOW_GRAPHIC_FOR_SHOWBUILDER = 'SHOW_GRAPHIC_FOR_SHOWBUILDER';
export const CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER = 'CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER';
export const DUPLICATE_OVERLAY = 'DUPLICATE_OVERLAY';
export const SET_DRAFT_OVERLAYS = 'SET_DRAFT_OVERLAYS';
export const CLEAR_DRAFT_OVERLAYS = 'CLEAR_DRAFT_OVERLAYS';

export const OVERLAY_ENTITY_UPDATED = EntityType.overlay;
export const OVERLAY_ENTITY_UPDATED_DRAFT = `${EntityType.overlay}-edit`;

interface AsyncAction {
	type: string;
	promise: Promise<any>
}

export function addGraphicsAction(
	token: string,
	graphics: GraphicInterface,
	onSuccess: Function,
): AsyncAction {
	return {
		type: ADD_GRAPHICS,
		promise: addGraphics(token, graphics, onSuccess),
	};
}

export function appendGraphicAction(graphics: GraphicInterface): Action {
	return {
		type: APPEND_GRAPHICS,
		payload: graphics,
	};
}

export function prependGraphicAction(graphics: GraphicInterface): Action {
	return {
		type: PREPEND_GRAPHICS,
		payload: graphics,
	};
}

export function editGraphicsAction(
	token: string,
	graphics: GraphicInterface,
	onSuccess: Function,
): AsyncAction {
	return {
		type: EDIT_GRAPHICS,
		promise: addGraphics(token, graphics, onSuccess),
	};
}

export function updateGraphicsAction(graphics: GraphicInterface): Action {
	return {
		type: UPDATE_GRAPHICS,
		payload: graphics,
	};
}

export function deleteGraphicsAction(
	token: string,
	graphics: GraphicInterface,
	onSuccess: Function,
): AsyncAction {
	return {
		type: DELETE_GRAPHICS,
		promise: deleteGraphics(token, graphics, onSuccess),
	};
}

export function loadGraphicsAction(token: string, meeting: number): AsyncAction {
	return {
		type: LOAD_GRAPHICS,
		promise: loadGraphics(token, meeting),
	};
}

export function removeGraphicAction(graphicId: number): Action {
	return {
		type: REMOVE_GRAPHICS,
		payload: graphicId,
	};
}

export function setEnabledGraphics(idArray: number[]): Action {
	return {
		type: SET_ENABLED_GRAPHICS,
		payload: idArray,
	};
}

export function addEnabledGraphic(id: number): Action {
	return {
		type: ADD_ENABLED_GRAPHIC,
		payload: id,
	};
}

export function removeEnabledGraphic(id: number): Action {
	return {
		type: REMOVE_ENABLED_GRAPHIC,
		payload: id,
	};
}

export function removeShotModalGraphic(graphicId: number): Action {
	return {
		type: REMOVE_SHOT_MODAL_GRAPHIC,
		payload: graphicId,
	};
}

export function previewAnimationIsPlaying(playing: boolean) {
	return {
		type: PLAYING_PREVIEW_ANIMATION,
		payload: playing,
	};
}

export function bulkEditGraphicsAction(
	token: string,
	meetingId: number,
	graphics: GraphicInterface[],
	onSuccess: Function = () => {},
): AsyncAction {
	return {
		type: BULK_EDIT_GRAPHICS,
		promise: bulkEditGraphics(token, meetingId, graphics, onSuccess),
	};
}

export function syncGraphicsReducerState(state: any): Action {
	return {
		type: SYNC_GRAPHICS_REDUCER_STATE,
		payload: state,
	};
}

export function addOverlaysUploadData(state: OverlayUploadData): Action {
	return {
		type: ADD_OVERLAYS_UPLOAD_DATA,
		payload: state,
	};
}

export function deleteOverlaysUploadData(payload: OverlayUploadData): Action {
	return {
		type: DELETE_OVERLAYS_UPLOAD_DATA,
		payload,
	};
}

export function setAnimatedGraphics(idArray: number[]): Action {
	return {
		type: SET_ANIMATED_GRAPHICS,
		payload: idArray,
	};
}

export function addAnimatedGraphic(id: number): Action {
	return {
		type: ADD_ANIMATED_GRAPHIC,
		payload: id,
	};
}

export function hideGraphicForShowBuilder(id: number): Action {
	return {
		type: HIDE_GRAPHIC_FOR_SHOWBUILDER,
		payload: id,
	};
}

export function showGraphicForShowBuilder(id: number): Action {
	return {
		type: SHOW_GRAPHIC_FOR_SHOWBUILDER,
		payload: id,
	};
}

export function clearHiddenGraphicsForShowBuilder(): Action {
	return {
		type: CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER,
		payload: null,
	};
}

export function duplicateOverlay(
	token: string,
	overlay: GraphicInterface,
	generatedContent?: GeneratedContent,
): AsyncAction {
	return {
		type: DUPLICATE_OVERLAY,
		promise: duplicateOverlayRequest(token, overlay, generatedContent),
	};
}

export function setDraftOverlays(draftOverlays: GraphicInterface[]): Action {
	return {
		type: SET_DRAFT_OVERLAYS,
		payload: draftOverlays,
	};
}

export function clearDraftOverlays(): Action {
	return {
		type: CLEAR_DRAFT_OVERLAYS,
		payload: null,
	};
}

export function setCurrentGraphicsId(id: number): Action {
	return {
		type: SET_CURRENT_GRAPHICS_ID,
		payload: id,
	};
}
